import { Link } from 'react-router';

import { noteRowPresentation, type NoteAction } from '../notes/intents';
import type { NoteSummary } from '../notes/schema';
import { Icon, type IconName } from '../ui/Icon';
import styles from './history.module.css';

export type RetryRowState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'refreshing' }
  | { readonly kind: 'error'; readonly message: string };

export interface NoteRowProps {
  readonly note: NoteSummary;
  /** Also drives the "Recuperar" button on a discarded row: both are one in-flight action per
   * row, and a row never offers both at once. */
  readonly retryState: RetryRowState;
  readonly onRetry: (noteId: string) => void;
  readonly onRestore?: (noteId: string) => void;
}

function formatTotal(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  return `$${value}`;
}

const TONE_CLASS: Readonly<Record<string, string | undefined>> = {
  neutral: styles.statusNeutral,
  info: styles.statusInfo,
  warning: styles.statusWarning,
  danger: styles.statusDanger,
  success: styles.statusOk,
};

/** The tone's glyph, reinforcing the phrase beside it - never replacing it. */
const TONE_ICON: Readonly<Record<string, IconName>> = {
  neutral: 'info-circle',
  info: 'info-circle',
  warning: 'alert-triangle',
  danger: 'alert-triangle',
  success: 'check-circle',
};

interface ActionButtonProps {
  readonly action: NoteAction;
  readonly variant: 'primary' | 'secondary';
  readonly retryPending: boolean;
  readonly onRetry: () => void;
  readonly onRestore: () => void;
}

function ActionButton({ action, variant, retryPending, onRetry, onRestore }: ActionButtonProps) {
  const className = variant === 'primary' ? styles.actionPrimary : styles.actionSecondary;
  if (action.kind === 'retry') {
    return (
      <button type="button" className={className} disabled={retryPending} onClick={onRetry}>
        {retryPending ? 'Reintentando…' : action.label}
      </button>
    );
  }
  if (action.kind === 'restore') {
    return (
      <button type="button" className={className} disabled={retryPending} onClick={onRestore}>
        {retryPending ? 'Recuperando…' : action.label}
      </button>
    );
  }
  return (
    <Link className={className} to={action.to ?? '#'}>
      {action.label}
    </Link>
  );
}

/**
 * One row of the note list. Thumbnail, customer/date/line-count/total stay visible (CLAUDE.md:
 * "the note's status is always visible later"); status reads as a toned icon plus an actionable
 * Spanish phrase, with at most two inline buttons for a row that needs owner action and a
 * chevron for one that is only informational.
 */
export function NoteRow({ note, retryState, onRetry, onRestore }: NoteRowProps) {
  const writtenTotal = formatTotal(note.written_total);
  const calculatedTotal = formatTotal(note.calculated_total);
  const totalsDisagree =
    writtenTotal !== null &&
    calculatedTotal !== null &&
    note.written_total !== note.calculated_total;
  const presentation = noteRowPresentation(note);
  const hasActions =
    presentation.primaryAction !== undefined || presentation.secondaryAction !== undefined;
  const retryPending = retryState.kind === 'pending' || retryState.kind === 'refreshing';

  return (
    <li className={styles.card}>
      <Link className={styles.cardTop} to={`/notas/${note.id}`}>
        {note.image_id !== null ? (
          <img
            className={styles.thumb}
            src={`/notes/${note.id}/image`}
            alt="Foto de la nota"
            loading="lazy"
          />
        ) : null}
        <div className={styles.cardBody}>
          <div className={styles.cardHeader}>
            <span className={styles.customer}>{note.customer ?? 'Sin cliente todavía'}</span>
            {/* The pad's own serial number, so a row here can be matched against the sheet of
                paper on the desk. Nothing else on the card identifies the physical note. */}
            {note.folio !== null ? <span className={styles.folio}>#{note.folio}</span> : null}
          </div>
          <span className={styles.meta}>
            {note.note_date ?? 'Sin fecha'} · {note.line_count}{' '}
            {note.line_count === 1 ? 'línea' : 'líneas'}
            {writtenTotal !== null ? ` · ${writtenTotal}` : ''}
          </span>
          {totalsDisagree ? (
            <span className={styles.issueNote}>calculado {calculatedTotal} — no cuadra</span>
          ) : null}
          <span className={`${styles.statusLine ?? ''} ${TONE_CLASS[presentation.tone] ?? ''}`}>
            <Icon
              className={styles.statusIcon}
              name={TONE_ICON[presentation.tone] ?? 'info-circle'}
              size={14}
              strokeWidth={2.2}
            />
            {presentation.message}
          </span>
          {presentation.helper !== undefined ? (
            <span className={styles.statusHelper}>{presentation.helper}</span>
          ) : null}
        </div>
        {!hasActions ? <Icon className={styles.chevron} name="chevron-right" size={18} /> : null}
      </Link>

      {hasActions ? (
        <div className={styles.rowActions}>
          {presentation.primaryAction !== undefined ? (
            <ActionButton
              action={presentation.primaryAction}
              variant="primary"
              retryPending={retryPending}
              onRetry={() => {
                onRetry(note.id);
              }}
              onRestore={() => {
                onRestore?.(note.id);
              }}
            />
          ) : null}
          {presentation.secondaryAction !== undefined ? (
            <ActionButton
              action={presentation.secondaryAction}
              variant="secondary"
              retryPending={retryPending}
              onRetry={() => {
                onRetry(note.id);
              }}
              onRestore={() => {
                onRestore?.(note.id);
              }}
            />
          ) : null}
        </div>
      ) : null}

      {retryState.kind === 'error' ? (
        <p className={styles.retryError} role="alert">
          {retryState.message}
        </p>
      ) : null}
      {retryState.kind === 'refreshing' ? (
        <p className={styles.retryInfo} role="status">
          Esta nota ya cambió. Actualizando su estado…
        </p>
      ) : null}
    </li>
  );
}
